/**
 * Centralised SweetAlert2 wrapper.
 *
 * Mirrors the API surface of the reference `swtAlert` from AL_Mabrook so
 * existing patterns continue to work, while pushing the styling through this
 * project's design tokens.
 *
 * Usage:
 *   swtAlert.success("Signed In", "Logged in successfully")
 *   swtAlert.error("Sign In Failed", "Invalid credentials")
 *   swtAlert.warning("Confirm Action", "Are you sure?", "Confirm", "Cancel")
 *   const loader = swtAlert.loader("Saving..."); ...; swtAlert.close();
 *
 * Prefer passing a (title, text) pair for clear, consistent popup headings.
 * A single message string is still supported as a fallback for old call sites.
 */

import Swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.min.css";
import { uploadingFile, wait } from "./sweetAlertHtmls";

const customClass = {
  popup: "swal_popup",
  title: "swal_title",
  htmlContainer: "swal_text",
  confirmButton: "app-swal-btn app-swal-btn--primary",
  cancelButton: "app-swal-btn app-swal-btn--secondary",
};

const DEFAULT_CONFIRM_TEXT = "OK";
const DEFAULT_REJECT_TEXT = "";
const SUCCESS_TIMER = 4000;

/** Backward compatibility for old single-message calls. New calls should pass
 *  both title and message, e.g. swtAlert.success("Saved", "Changes saved."). */
const normalizeArgs = (title, text) => {
  if (text === undefined && typeof title === "string") {
    return { title: undefined, text: title };
  }
  return { title, text };
};

const buildAlertConfig = ({
  icon,
  title,
  defaultTitle,
  text,
  html,
  confirmText = DEFAULT_CONFIRM_TEXT,
  rejectButtonText = DEFAULT_REJECT_TEXT,
  timer,
  timerProgressBar = false,
  showCloseButton = false,
}) => {
  const normalizedRejectButtonText =
    typeof rejectButtonText === "string" ? rejectButtonText.trim() : "";

  return {
    icon,
    title: title ?? defaultTitle,
    text,
    html,
    timer,
    timerProgressBar,
    confirmButtonText: confirmText,
    showCancelButton: !!normalizedRejectButtonText,
    cancelButtonText: normalizedRejectButtonText,
    showDenyButton: false,
    denyButtonText: "",
    allowOutsideClick: false,
    allowEscapeKey: true,
    showCloseButton,
    customClass: { ...customClass },
    buttonsStyling: false,
    didOpen: () => {
      const container = Swal.getContainer();
      if (container) container.style.pointerEvents = "auto";
    },
  };
};

const fireProgressAlert = ({ htmlContent, titlePrefix }) => {
  let intervalId;
  return Swal.fire({
    html: htmlContent,
    allowOutsideClick: false,
    showConfirmButton: false,
    backdrop: true,
    customClass: { ...customClass },
    didOpen: () => {
      const titleElement = document.getElementById("uploading-title");
      const progressBar = document.querySelector(".progress-bar");
      if (!titleElement || !progressBar) return;

      let progress = 0;
      let dotCount = 1;
      intervalId = window.setInterval(() => {
        dotCount = (dotCount % 3) + 1;
        titleElement.textContent = `${titlePrefix}${" .".repeat(dotCount)}`;
        if (progress < 100) {
          progress += 10;
          progressBar.style.width = `${progress}%`;
          return;
        }
        window.clearInterval(intervalId);
      }, 500);
    },
    willClose: () => {
      if (intervalId) window.clearInterval(intervalId);
    },
  });
};

const swtAlert = {
  success: (
    title,
    text,
    confirmText = DEFAULT_CONFIRM_TEXT,
    rejectButtonText = DEFAULT_REJECT_TEXT
  ) => {
    const args = normalizeArgs(title, text);
    return Swal.fire(
      buildAlertConfig({
        icon: "success",
        title: args.title,
        defaultTitle: "Success",
        text: args.text,
        confirmText,
        rejectButtonText,
        timer: SUCCESS_TIMER,
        timerProgressBar: true,
        showCloseButton: true,
      })
    );
  },

  error: (
    title,
    text,
    confirmText = DEFAULT_CONFIRM_TEXT,
    rejectButtonText = DEFAULT_REJECT_TEXT
  ) => {
    const args = normalizeArgs(title, text);
    return Swal.fire(
      buildAlertConfig({
        icon: "error",
        title: args.title,
        defaultTitle: "Error",
        text: args.text,
        confirmText,
        rejectButtonText,
      })
    );
  },

  info: (
    title,
    text,
    confirmText = DEFAULT_CONFIRM_TEXT,
    rejectButtonText = DEFAULT_REJECT_TEXT
  ) => {
    const args = normalizeArgs(title, text);
    return Swal.fire(
      buildAlertConfig({
        icon: "info",
        title: args.title,
        defaultTitle: "Info",
        text: args.text,
        confirmText,
        rejectButtonText,
      })
    );
  },

  warning: (
    title,
    text,
    confirmText = DEFAULT_CONFIRM_TEXT,
    rejectButtonText = DEFAULT_REJECT_TEXT
  ) => {
    const args = normalizeArgs(title, text);
    return Swal.fire(
      buildAlertConfig({
        icon: "warning",
        title: args.title,
        defaultTitle: "Warning",
        text: args.text,
        confirmText,
        rejectButtonText,
      })
    );
  },

  warningHtml: (
    title = "Warning",
    html = "",
    confirmText = DEFAULT_CONFIRM_TEXT,
    rejectButtonText = DEFAULT_REJECT_TEXT
  ) =>
    Swal.fire(
      buildAlertConfig({
        icon: "warning",
        title,
        defaultTitle: "Warning",
        html,
        confirmText,
        rejectButtonText,
      })
    ),

  loader: (title = "Please wait...", text = "") =>
    Swal.fire({
      title,
      text,
      allowOutsideClick: false,
      allowEscapeKey: false,
      customClass: { ...customClass },
      buttonsStyling: false,
      didOpen: () => {
        const container = Swal.getContainer();
        if (container) container.style.pointerEvents = "auto";
        Swal.showLoading();
      },
    }),

  uploading: () =>
    fireProgressAlert({
      htmlContent: uploadingFile(),
      titlePrefix: "Uploading",
    }),

  waiting: () =>
    fireProgressAlert({
      htmlContent: wait(),
      titlePrefix: "Please wait",
    }),

  close: () => Swal.close(),
};

export default swtAlert;
